import { createSlice, Draft, isAnyOf, PayloadAction } from '@reduxjs/toolkit';
import {
  AnemiaProtocolStatus,
  CardiacStressTestProtocolStatus,
  CombinedProtocolStatus,
  EarlyPostopProtocolStatus,
  NicotineProtocolStatus,
  NutritionSupportProtocolStatus,
  PostopChronicPainProtocolStatus,
  PreopLabTestProtocolStatus,
  protocolApi,
  ProtocolName,
  SleepApneaProtocolStatus,
  SurgeryPlanningProtocol,
  TelehealthProtocolStatus
} from '@/modules/protocol';
import {
  ConsultationDateStatus,
  MedicalHoItemCategory,
  MedicalScoreName,
  Medication,
  medsApi,
  Patient,
  PeropStatus,
  PostopStatus,
  PremedicationOrder,
  PreopSortBy,
  PreopStatus,
  PreopView,
  SurgeryDateStatus,
  surgicalEventApi
} from '@/modules/surgicalEvent';
import type {
  AnemiaTableStatuses,
  CardiacStressTestTableStatuses,
  EarlyPostopTableStatuses,
  LabTestingTableStatuses,
  OsaTableStatuses,
  TelehealthTableStatuses
} from '@/modules/dashboard';
import isEmpty from 'lodash/fp/isEmpty';
import xor from 'lodash/fp/xor';
import { ID, User } from '@ianesthesie-kit/services';
import { ChipProps } from '@mui/material';
import { isNotEmpty } from '@/util';
import { EpicDocument, epicSlice } from '@/modules/epic';
import {
  defaultMatrixValue,
  deserialize,
  INPUT_MAX_LEN,
  removeMatrixValue
} from './medicationDosageMatrixUtils';
import { doctorBffApi } from '@/modules';
import isNil from 'lodash/fp/isNil';
import omitBy from 'lodash/fp/omitBy';
import { pingCurrentUser } from '@ianesthesie-kit/redux';
import { isUserRestricted } from '@/hooks';
import type {
  DosageMatrixValue,
  TimeOfDay
} from './medicationDosageMatrixTypes';

export enum TriageGroup {
  POSTOP = 'POSTOP',
  POSTOP_EARLY = 'POSTOP_EARLY',
  POSTOP_CHRONIC_PAIN = 'POSTOP_CHRONIC_PAIN',
  PEROP = 'PEROP',
  PREOP_ALL = 'PREOP_ALL',
  PREOP_ANEMIA = 'PREOP_ANEMIA',
  PREOP_SMOKERS = 'PREOP_SMOKERS',
  PREOP_SLEEP_APNEA = 'PREOP_SLEEP_APNEA',
  PREOP_MALNUTRITION = 'PREOP_MALNUTRITION',
  PREOP_TELEHEALTH = 'PREOP_TELEHEALTH',
  PREOP_LAB_TESTING = 'PREOP_LAB_TESTING',
  PREOP_CARDIAC_STRESS_TEST = 'PREOP_CARDIAC_STRESS_TEST',
  PREOP_SURGERY_PLANNING = 'PREOP_SURGERY_PLANNING',
  PREOP_CHRONIC_PAIN = 'PREOP_CHRONIC_PAIN'
}

export type DashboardGroupingStatus =
  | CombinedProtocolStatus
  | SurgeryDateStatus
  | ConsultationDateStatus
  | PeropStatus
  | TelehealthTableStatuses
  | OsaTableStatuses
  | LabTestingTableStatuses
  | CardiacStressTestTableStatuses
  | AnemiaTableStatuses
  | EarlyPostopTableStatuses;

// TODO refactor this; hold structured data

export const toProtocolStatuses = <
  R extends CombinedProtocolStatus = CombinedProtocolStatus
>(
  dashboardStatus?: DashboardGroupingStatus,
  triageGroup?: TriageGroup
): R | R[] | undefined => {
  if (!dashboardStatus || !triageGroup) {
    return undefined;
  }
  if (triageGroup === TriageGroup.PREOP_TELEHEALTH) {
    if (dashboardStatus === 'PENDING_VALIDATION') {
      return [
        TelehealthProtocolStatus.AUTO_ELIGIBLE_FOR_TELEVISIT,
        TelehealthProtocolStatus.AUTO_UNKNOWN_ELIGIBILITY
      ] as R[];
    }

    if (dashboardStatus === 'FAILED') {
      return [
        TelehealthProtocolStatus.TELEVISIT_FAILED_APPOINTMENT_NOT_BOOKED,
        TelehealthProtocolStatus.TELEVISIT_FAILED_NO_SHOW,
        TelehealthProtocolStatus.TELEVISIT_FAILED_NEED_PHYSICAL_VISIT,
        TelehealthProtocolStatus.TELEVISIT_FAILED_OTHER
      ] as R[];
    }

    if (dashboardStatus === 'TELEHEALTH_DONE') {
      return [
        TelehealthProtocolStatus.TELEVISIT_DONE_INFORMED_CONSENT_SENT,
        TelehealthProtocolStatus.TELEVISIT_DONE_INFORMED_CONSENT_SIGNED,
        TelehealthProtocolStatus.COMPLETE_REPORT_SENT
      ] as R[];
    }
  } else if (triageGroup === TriageGroup.PREOP_LAB_TESTING) {
    if (dashboardStatus === 'DONE') {
      return [
        PreopLabTestProtocolStatus.PREOPERATIVE_LAB_TEST_NOT_RECOMMENDED,
        PreopLabTestProtocolStatus.PREOPERATIVE_LAB_TEST_ALL_DONE_AND_REVIEWED,
        PreopLabTestProtocolStatus.PREOPERATIVE_LAB_TEST_COMPLETED
      ] as R[];
    }
  } else if (triageGroup === TriageGroup.PREOP_CARDIAC_STRESS_TEST) {
    if (dashboardStatus === 'TO_EVALUATE') {
      return [
        CardiacStressTestProtocolStatus.CARDIAC_STRESS_TEST_AUTO_RECOMMENDED,
        CardiacStressTestProtocolStatus.CARDIAC_STRESS_TEST_AUTO_UNKNOWN_ELIGIBILITY,
        CardiacStressTestProtocolStatus.CARDIAC_STRESS_TEST_AUTO_ALREADY_UPLOADED
      ] as R[];
    }

    if (dashboardStatus === 'PENDING') {
      return [
        CardiacStressTestProtocolStatus.CARDIAC_STRESS_TEST_WAITING_FOR_APPOINTMENT,
        CardiacStressTestProtocolStatus.CARDIAC_STRESS_TEST_SCHEDULED,
        CardiacStressTestProtocolStatus.CARDIAC_STRESS_TEST_WAITING_FOR_RESULTS,
        CardiacStressTestProtocolStatus.CARDIAC_STRESS_TEST_RESULTS_RECEIVED
      ] as R[];
    }

    if (dashboardStatus === 'CLEARED') {
      return [
        CardiacStressTestProtocolStatus.CARDIAC_STRESS_TEST_DONE_CLEARED,
        CardiacStressTestProtocolStatus.CARDIAC_STRESS_TEST_NOT_REQUIRED
      ] as R[];
    }
  } else if (triageGroup === TriageGroup.PREOP_SLEEP_APNEA) {
    if (dashboardStatus === 'DIAGNOSED') {
      return [
        SleepApneaProtocolStatus.OSA_DIAGNOSED_WITH_CPAP,
        SleepApneaProtocolStatus.OSA_DIAGNOSED_WITHOUT_CPAP
      ] as R[];
    }

    if (dashboardStatus === 'DONE') {
      return [
        SleepApneaProtocolStatus.OSA_HIGH_RISK_REFERRAL_DONE,
        SleepApneaProtocolStatus.OSA_MEDIUM_RISK_REFERRAL_DONE,
        SleepApneaProtocolStatus.OSA_RISK_IDENTIFIED_NO_ACTION
      ] as R[];
    }
  } else if (triageGroup === TriageGroup.PREOP_MALNUTRITION) {
    if (dashboardStatus === 'DONE') {
      return [
        NutritionSupportProtocolStatus.UNDER_NUTRITION_REFERRAL_DONE,
        NutritionSupportProtocolStatus.UNDER_NUTRITION_PROTEIN_SENT_AND_REFERRAL_DONE
      ] as R[];
    }
  } else if (triageGroup === TriageGroup.PREOP_SMOKERS) {
    if (dashboardStatus === 'DONE') {
      return [
        NicotineProtocolStatus.NICOTINE_PATCH_DONE,
        NicotineProtocolStatus.NICOTINE_PATCH_AND_SPECIALIST_REFERRAL_DONE,
        NicotineProtocolStatus.NICOTINE_PATCH_AND_SPECIALIST_REFERRAL_MANUALLY_DONE
      ] as R[];
    }
  } else if (triageGroup === TriageGroup.PREOP_ANEMIA) {
    if (dashboardStatus === 'TO_CHECK') {
      return [
        AnemiaProtocolStatus.AUTO_POTENTIALLY_ANEMIC,
        AnemiaProtocolStatus.AUTO_ANEMIA_NOT_DETECTED,
        AnemiaProtocolStatus.AUTO_ANEMIA_DETECTED,
        AnemiaProtocolStatus.AUTO_IRON_INJECTION_PRESCRIPTION_RECOMMENDED,
        AnemiaProtocolStatus.AUTO_EPO_AND_IRON_INJECTION_PRESCRIPTION_RECOMMENDED,
        AnemiaProtocolStatus.AUTO_EXPERT_REFERRAL_RECOMMENDED
      ] as R[];
    }

    if (dashboardStatus === 'PENDING') {
      return [
        AnemiaProtocolStatus.EXTRA_LAB_TESTS_REQUESTED,
        AnemiaProtocolStatus.IRON_INJECTION_PRESCRIPTION_SENT,
        AnemiaProtocolStatus.IRON_INJECTION_AND_EPO_PRESCRIPTIONS_SENT,
        AnemiaProtocolStatus.IRON_INJECTION_SCHEDULED
      ] as R[];
    }

    if (dashboardStatus === 'DONE') {
      return [
        AnemiaProtocolStatus.EXPERT_REFERRAL_SENT,
        AnemiaProtocolStatus.EPO_PRESCRIPTION_SENT,
        AnemiaProtocolStatus.IRON_INJECTIONS_DONE,
        AnemiaProtocolStatus.ANEMIA_CONFIRMED_NO_ACTION_REQUIRED,
        AnemiaProtocolStatus.ANEMIA_CONFIRMED_OTHER,
        AnemiaProtocolStatus.ANEMIA_NOT_CONFIRMED
      ] as R[];
    }
  } else if (triageGroup === TriageGroup.POSTOP_EARLY) {
    if (dashboardStatus === 'DONE') {
      return [
        EarlyPostopProtocolStatus.DONE_SUCCESS,
        EarlyPostopProtocolStatus.DONE_FAILED
      ] as R[];
    }
  } else if (triageGroup === TriageGroup.POSTOP_CHRONIC_PAIN) {
    if (dashboardStatus === PostopChronicPainProtocolStatus.DN4_POSITIVE) {
      return [
        PostopChronicPainProtocolStatus.DN4_POSITIVE,
        PostopChronicPainProtocolStatus.AUTO_DN4_POSITIVE
      ] as R[];
    } else if (
      dashboardStatus === PostopChronicPainProtocolStatus.DN4_VAS_PAIN
    ) {
      return [
        PostopChronicPainProtocolStatus.DN4_VAS_PAIN,
        PostopChronicPainProtocolStatus.AUTO_DN4_VAS_PAIN
      ] as R[];
    }
  }

  return dashboardStatus as R;
};

export const toDashboardStatus = <
  R extends CombinedProtocolStatus = CombinedProtocolStatus
>(
  protocolStatus?: R | R[] | PreopStatus | PostopStatus | PeropStatus,
  triageGroup?: TriageGroup
): DashboardGroupingStatus | undefined => {
  if (!protocolStatus || !triageGroup) {
    return undefined;
  }

  const arraysEqual = (arr1: Array<any>, arr2: Array<any>) =>
    isEmpty(xor(arr1, arr2));
  if (Array.isArray(protocolStatus)) {
    if (triageGroup === TriageGroup.PREOP_TELEHEALTH) {
      if (
        arraysEqual(
          protocolStatus,
          toProtocolStatuses('PENDING_VALIDATION', triageGroup)
        )
      ) {
        return 'PENDING_VALIDATION';
      }
      if (
        arraysEqual(protocolStatus, toProtocolStatuses('FAILED', triageGroup))
      ) {
        return 'FAILED';
      }
      if (
        arraysEqual(
          protocolStatus,
          toProtocolStatuses('TELEHEALTH_DONE', triageGroup)
        )
      ) {
        return 'TELEHEALTH_DONE';
      }
    } else if (triageGroup === TriageGroup.PREOP_LAB_TESTING) {
      if (
        arraysEqual(protocolStatus, toProtocolStatuses('DONE', triageGroup))
      ) {
        return 'DONE';
      }
    } else if (triageGroup === TriageGroup.PREOP_CARDIAC_STRESS_TEST) {
      if (
        arraysEqual(
          protocolStatus,
          toProtocolStatuses('TO_EVALUATE', triageGroup)
        )
      ) {
        return 'TO_EVALUATE';
      }

      if (
        arraysEqual(protocolStatus, toProtocolStatuses('PENDING', triageGroup))
      ) {
        return 'PENDING';
      }

      if (
        arraysEqual(protocolStatus, toProtocolStatuses('CLEARED', triageGroup))
      ) {
        return 'CLEARED';
      }
    } else if (triageGroup === TriageGroup.PREOP_SLEEP_APNEA) {
      if (
        arraysEqual(
          protocolStatus,
          toProtocolStatuses('DIAGNOSED', triageGroup)
        )
      ) {
        return 'DIAGNOSED';
      }

      if (
        arraysEqual(protocolStatus, toProtocolStatuses('DONE', triageGroup))
      ) {
        return 'DONE';
      }
    } else if (triageGroup === TriageGroup.PREOP_MALNUTRITION) {
      if (
        arraysEqual(protocolStatus, toProtocolStatuses('DONE', triageGroup))
      ) {
        return 'DONE';
      }
    } else if (triageGroup === TriageGroup.PREOP_SMOKERS) {
      if (
        arraysEqual(protocolStatus, toProtocolStatuses('DONE', triageGroup))
      ) {
        return 'DONE';
      }
    } else if (triageGroup === TriageGroup.PREOP_ANEMIA) {
      if (
        arraysEqual(protocolStatus, toProtocolStatuses('TO_CHECK', triageGroup))
      ) {
        return 'TO_CHECK';
      }

      if (
        arraysEqual(protocolStatus, toProtocolStatuses('PENDING', triageGroup))
      ) {
        return 'PENDING';
      }

      if (
        arraysEqual(protocolStatus, toProtocolStatuses('DONE', triageGroup))
      ) {
        return 'DONE';
      }
    } else if (triageGroup === TriageGroup.POSTOP_EARLY) {
      if (
        arraysEqual(protocolStatus, toProtocolStatuses('DONE', triageGroup))
      ) {
        return 'DONE';
      }
    }
  }

  return protocolStatus as DashboardGroupingStatus;
};

export type TablePagination = {
  page: number;
  pageSize: number;
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
};

export type TriagePagination = {
  [group in TriageGroup]?: {
    [status in DashboardGroupingStatus]?: TablePagination;
  };
};

export type TriagePaginationPayload = {
  group: TriageGroup;
  status: DashboardGroupingStatus;
  page?: number;
  pageSize?: number;
};

export type DashboardSortBy = PreopSortBy;

export type TriageSortingPayload = {
  group: TriageGroup;
  status: DashboardGroupingStatus;
  sortBy?: DashboardSortBy;
  sortDirection?: 'asc' | 'desc';
};

export type PatientSearchType = 'protocol' | 'global';

type TriageContext = {
  editable: {
    emergency: boolean;
    medicalFields: boolean;
    identity: boolean;
    surgeryDate: boolean;
    medicalDocuments: boolean;
  };
  scratchConsultationEligibility: {
    isNotSeen: boolean;
    isUserNotRestricted: boolean;
  };
  context: {
    group?: TriageGroup;
    status?: DashboardGroupingStatus;
    statusOrder?: string[];
    specificDate?: string;
    /** copy of userConfig.settings.preopView; copied on appContextSlice extra reducers */
    preopView?: PreopView;
  };
  protocolSearch?: string;
  pagination: TriagePagination;
  lastSpecificDatePagination?: Partial<TablePagination>;
};

type ProtocolDrawerState = {
  open: boolean;
  type?: ProtocolName;
  headerChipProps?: ChipProps;
  data?: {
    patient: Patient;
    targetStatus: CombinedProtocolStatus;
    manualOverride?: boolean;
  } & Record<string, any>;
};

export enum MedicalTemplateType {
  COVER_LETTER = 'TEMPLATE_COVER_LETTER',
  PREOP_EVALUATION = 'TEMPLATE_PREOP_EVALUATION',
  POSTOP_PAIN_CLINICAL_NOTE = 'TEMPLATE_POSTOP_PAIN_CLINICAL_NOTE',
  PREOP_EVAL_PER_HOSPITAL = 'PREOP_EVAL_PER_HOSPITAL'
}

type TemplateDrawerState = {
  open: boolean;
  templateType?: MedicalTemplateType;
  patientId?: ID;
  hideTemplateSelector?: boolean;
  titleTranslationId?: string;
  title?: string;
};

export type InsertionType = 'patient' | 'pendingPatient';

type CreatePatientDrawerState = {
  open: boolean;
  insertionType: InsertionType;
  formValues: Record<string, any>;
};

type MedicalScoresDrawerState = {
  open: boolean;
  scoreName?: MedicalScoreName;
  patient?: Patient;
  preferShowDraft?: boolean;
};

type ScSuggestionDrawerState = {
  open: boolean;
  patient?: Patient;
  category?: MedicalHoItemCategory;
  suggestionId?: string;
  payload?: Record<string, string | number | string[]>;
  notes?: string;
};

type MedicationDrawerState = {
  open: boolean;
  medication?: Partial<Medication>;
  patient?: Patient | ID;
  mode: 'newMedication' | 'editMedication';
  applyAutomation: boolean;
  automationAvailable: boolean;
  dryRunStatus: 'idle' | 'loading' | 'error';
  dryRunCompleted: boolean;
  automationOverridden: boolean;
  persistedMedication?: Medication;
  dosageMatrix?: DosageMatrixValue;
  entryHash?: string; // a unique value to identify the subsequent entries. Updated on clear
};

type OpenMedicationDrawerPayload = Pick<MedicationDrawerState, 'patient'> &
  Partial<
    Omit<MedicationDrawerState, 'open' | 'patient' | 'persistedMedication'>
  >;

type PremedicationDrawerState = {
  open: boolean;
  patient?: Patient | ID;
  order?: Partial<PremedicationOrder>;
  mode: 'newPremedication' | 'editPremedication';
};

type OpenPremedicationDrawerPayload = Pick<
  PremedicationDrawerState,
  'patient'
> &
  Partial<Omit<PremedicationDrawerState, 'open' | 'patient'>>;

export type EpicDocumentsDrawerState = {
  open: boolean;
  epicDocument?: EpicDocument | number | string;
  patient?: Patient | ID;
  nlpView: boolean;
  createMode: boolean;
  tmpClinicalNote?: string;
  tmpClinicalNoteTitle?: string;
  tmpEncounterId?: string;
  nlpFindingIdPerDocument?: Record<string, string | number>;
};

export type OpenEpicDocumentDrawerPayload = Pick<
  EpicDocumentsDrawerState,
  'patient'
> &
  Partial<Omit<EpicDocumentsDrawerState, 'open' | 'createMode' | 'patient'>>;

type MedicationInstructionsDrawerState = {
  open: boolean;
  patient?: Patient | ID;
  initialText?: string;
  editedText?: string;
};

type PatientsAppDetailsDrawerState = {
  open: boolean;
  patientId?: ID;
};

type CustomDocumentDrawerState = {
  open: boolean;
  patientId?: ID;
};

type BillingPatientDrawerState = {
  open: boolean;
  patientId?: ID;
  /**
   * Header identity. `BillingLineDto` carries no patient name, so the opener (the chase
   * dashboard row) hands it over rather than making the drawer fetch the patient just to
   * render a title.
   */
  patientName?: string;
};

interface AppContextState {
  currentHospital: ID | undefined;
  // this is temporary flag to listen for surgery protocol status updates, until websockets are integrated
  _tempListenForSurgeryPlanningProtocolStatusUpdates?: boolean;
  triage: TriageContext;
  protocolDrawer: ProtocolDrawerState;
  templateDrawer: TemplateDrawerState;
  newPatientDrawer: CreatePatientDrawerState;
  medicalScoresDrawer: MedicalScoresDrawerState;
  scSuggestionDrawer: ScSuggestionDrawerState;
  medicationDrawer: MedicationDrawerState;
  premedicationDrawer: PremedicationDrawerState;
  epicDocumentsDrawer: EpicDocumentsDrawerState;
  medicationInstructionsDrawer: MedicationInstructionsDrawerState;
  patientsAppDetailsDrawer: PatientsAppDetailsDrawerState;
  customDocumentDrawer: CustomDocumentDrawerState;
  billingPatientDrawer: BillingPatientDrawerState;
  communication: {
    initialMessage?: string;
  };
}

const defaultEditingState = true;

const initialState: AppContextState = {
  currentHospital: undefined,
  medicalScoresDrawer: {
    open: false
  },
  scSuggestionDrawer: {
    open: false
  },
  protocolDrawer: {
    open: false
  },
  templateDrawer: {
    open: false
  },
  newPatientDrawer: {
    open: false,
    insertionType: 'patient',
    formValues: {}
  },
  medicationDrawer: {
    open: false,
    mode: 'newMedication',
    applyAutomation: true,
    automationAvailable: true,
    dryRunStatus: 'idle',
    dryRunCompleted: false,
    automationOverridden: false
  },
  premedicationDrawer: {
    open: false,
    mode: 'newPremedication'
  },
  epicDocumentsDrawer: {
    open: false,
    createMode: true,
    nlpView: false
  },
  medicationInstructionsDrawer: {
    open: false
  },
  patientsAppDetailsDrawer: {
    open: false
  },
  customDocumentDrawer: { open: false },
  billingPatientDrawer: { open: false },
  triage: {
    editable: {
      emergency: defaultEditingState,
      medicalFields: defaultEditingState,
      identity: defaultEditingState,
      surgeryDate: defaultEditingState,
      medicalDocuments: defaultEditingState
    },
    scratchConsultationEligibility: {
      isNotSeen: false,
      isUserNotRestricted: false
    },
    context: {
      group: undefined,
      status: undefined
    },
    pagination: {}
  },
  communication: {}
};

const setAllDrawersClosed = (state: Draft<AppContextState>): void => {
  state.templateDrawer.open = false;
  state.templateDrawer.patientId = undefined;
  state.protocolDrawer.open = false;
  state.protocolDrawer.headerChipProps = undefined;
  state.protocolDrawer.data = undefined;
  state.protocolDrawer.type = undefined;
  state.newPatientDrawer.open = false;
  state.medicalScoresDrawer.open = false;
  state.medicalScoresDrawer.patient = undefined;
  state.scSuggestionDrawer.open = false;
  // state.scSuggestionDrawer.patient = undefined;
  state.medicationDrawer.open = false;
  state.medicationDrawer.patient = undefined;
  state.premedicationDrawer.open = false;
  state.premedicationDrawer.patient = undefined;
  state.epicDocumentsDrawer.open = false;
  state.epicDocumentsDrawer.patient = undefined;
  state.epicDocumentsDrawer.epicDocument = undefined;
  state.medicationInstructionsDrawer.open = false;
  // Keep patient, initialText, and editedText preserved when closing
  state.patientsAppDetailsDrawer.open = false;
  state.patientsAppDetailsDrawer.patientId = undefined;
  state.customDocumentDrawer.open = false;
  state.customDocumentDrawer.patientId = undefined;
  // Payload kept on purpose (same reason as scSuggestionDrawer above): the drawer stays
  // mounted through the ~250ms slide-out, and clearing it would repaint the closing panel
  // as an empty "unknown patient" state. The next open overwrites it anyway.
  state.billingPatientDrawer.open = false;
};

const updateMedicationDrawerDerivedState = (state: MedicationDrawerState) => {
  const prevAvailable = state.automationAvailable;
  const available =
    isNotEmpty(state.medication?.automationPayload) ||
    isNotEmpty(state.medication?.missingAutomationProperties) ||
    (state.mode === 'newMedication' && !!state.medication?.atc);
  state.automationAvailable = available;
  if (!state.persistedMedication?.id) {
    if (!available) {
      state.applyAutomation = false;
    } else if (!prevAvailable) {
      state.applyAutomation = true;
    }
  }

  // TODO this fails for PLAVIX check it out
  state.mode = state.medication?.id ? 'editMedication' : 'newMedication';
};

const deserializeDosageAndMatrixValues = (medication: Partial<Medication>) => {
  const matrixValue = deserialize(medication.dosage);
  const dosage = removeMatrixValue(medication.dosage, matrixValue);
  return {
    matrixValue,
    dosage
  };
};

const mergeMedicationDrawerMedication = (
  state: Draft<AppContextState>,
  payload: Partial<Medication> | undefined
): void => {
  if (payload) {
    state.medicationDrawer.medication = {
      ...state.medicationDrawer.medication,
      ...payload
    };
  } else {
    state.medicationDrawer.medication = undefined;
  }
  updateMedicationDrawerDerivedState(state.medicationDrawer);
};

export const appContextSlice = createSlice({
  name: 'appContext',
  initialState,
  reducers: {
    _setTempListenForSurgeryPlanningProtocolStatusUpdates: (
      state: Draft<AppContextState>,
      action: PayloadAction<boolean>
    ) => {
      state._tempListenForSurgeryPlanningProtocolStatusUpdates = action.payload;
    },
    closeAllDrawers: (state: Draft<AppContextState>) => {
      setAllDrawersClosed(state);
    },
    openEpicDocumentsDrawer: (
      state: Draft<AppContextState>,
      action: PayloadAction<OpenEpicDocumentDrawerPayload>
    ) => {
      Object.assign(state.epicDocumentsDrawer, action.payload);
      state.epicDocumentsDrawer.open = true;
      state.epicDocumentsDrawer.createMode = !action.payload.epicDocument;
      state.epicDocumentsDrawer.nlpFindingIdPerDocument =
        action.payload.nlpFindingIdPerDocument;
    },
    setEpicDocumentDrawerDocument: (
      state: Draft<AppContextState>,
      action: PayloadAction<EpicDocument>
    ) => {
      state.epicDocumentsDrawer.epicDocument = action.payload;
    },
    setEpicDocumentDrawerNlpView: (
      state: Draft<AppContextState>,
      action: PayloadAction<boolean>
    ) => {
      state.epicDocumentsDrawer.nlpView = action.payload;
    },
    updateEpicClinicalNote: (
      state: Draft<AppContextState>,
      action: PayloadAction<string>
    ) => {
      state.epicDocumentsDrawer.tmpClinicalNote = action.payload;
    },
    updateEpicClinicalNoteTitle: (
      state: Draft<AppContextState>,
      action: PayloadAction<string>
    ) => {
      state.epicDocumentsDrawer.tmpClinicalNoteTitle = action.payload;
    },
    updateEpicClinicalNoteEncounterId: (
      state: Draft<AppContextState>,
      action: PayloadAction<string>
    ) => {
      state.epicDocumentsDrawer.tmpEncounterId = action.payload;
    },
    closeEpicDocumentsDrawer: (state: Draft<AppContextState>) => {
      state.epicDocumentsDrawer.open = false;
      state.epicDocumentsDrawer.patient = undefined;
      state.epicDocumentsDrawer.epicDocument = undefined;
    },
    openMedicationInstructionsDrawer: (
      state: Draft<AppContextState>,
      action: PayloadAction<{ patient: Patient | ID; initialText: string }>
    ) => {
      // Check if patient has changed BEFORE closing drawers (which resets patient)
      const prevPatient = state.medicationInstructionsDrawer.patient;
      const newPatient = action.payload.patient;
      const prevPatientId =
        typeof prevPatient === 'object' ? prevPatient?.id : prevPatient;
      const newPatientId =
        typeof newPatient === 'object' ? newPatient?.id : newPatient;
      const isSamePatient = prevPatientId === newPatientId;

      setAllDrawersClosed(state);

      state.medicationInstructionsDrawer = {
        open: true,
        patient: action.payload.patient,
        initialText: action.payload.initialText,
        editedText:
          isSamePatient && state.medicationInstructionsDrawer.editedText
            ? state.medicationInstructionsDrawer.editedText
            : action.payload.initialText
      };
    },
    closeMedicationInstructionsDrawer: (state: Draft<AppContextState>) => {
      state.medicationInstructionsDrawer.open = false;
    },
    updateMedicationInstructionsText: (
      state: Draft<AppContextState>,
      action: PayloadAction<string>
    ) => {
      state.medicationInstructionsDrawer.editedText = action.payload;
    },
    openMedicationDrawer: (
      state: Draft<AppContextState>,
      action: PayloadAction<OpenMedicationDrawerPayload>
    ) => {
      setAllDrawersClosed(state);
      if (
        state.medicationDrawer.medication?.id &&
        !action.payload.medication?.id
      ) {
        state.medicationDrawer = initialState.medicationDrawer;
      }
      state.medicationDrawer = {
        ...state.medicationDrawer,
        ...action.payload,
        open: true,
        dryRunCompleted: false,
        automationOverridden: false
      };

      if (action.payload.medication?.id) {
        state.medicationDrawer.applyAutomation = false;
        state.medicationDrawer.persistedMedication = action.payload
          .medication as Medication;
        const { matrixValue, dosage } = deserializeDosageAndMatrixValues(
          action.payload.medication
        );
        state.medicationDrawer.dosageMatrix = matrixValue ?? defaultMatrixValue;
        if (matrixValue) {
          const med = {
            ...action.payload.medication
          } as Medication;
          med.dosage = dosage;
          state.medicationDrawer.medication = med;
        }
      } else {
        state.medicationDrawer.persistedMedication = undefined;
      }
      // state.medicationDrawer.medicationDirty = isMedicationDirty(
      //   state.medicationDrawer
      // );
      updateMedicationDrawerDerivedState(state.medicationDrawer);
    },
    closeMedicationDrawer: (
      state: Draft<AppContextState>,
      action: PayloadAction<boolean | undefined>
    ) => {
      // if clearState
      if (action.payload) {
        state.medicationDrawer = initialState.medicationDrawer;
      } else {
        state.medicationDrawer = {
          ...state.medicationDrawer,
          open: false,
          patient: undefined
        };
      }
    },
    openPremedicationDrawer: (
      state: Draft<AppContextState>,
      action: PayloadAction<OpenPremedicationDrawerPayload>
    ) => {
      setAllDrawersClosed(state);
      state.premedicationDrawer = {
        ...state.premedicationDrawer,
        ...action.payload,
        mode: action.payload.order?.id
          ? 'editPremedication'
          : 'newPremedication',
        open: true
      };
    },
    closePremedicationDrawer: (state: Draft<AppContextState>) => {
      state.premedicationDrawer = { ...initialState.premedicationDrawer };
    },
    clearMedicationDrawer: (state: Draft<AppContextState>) => {
      state.medicationDrawer = {
        ...initialState.medicationDrawer,
        dosageMatrix: defaultMatrixValue,
        patient: state.medicationDrawer.patient,
        open: state.medicationDrawer.open,
        entryHash: Math.random().toString(36).slice(2, 7)
      };
      updateMedicationDrawerDerivedState(state.medicationDrawer);
    },
    updateMedicationDrawerMedication: (
      state: Draft<AppContextState>,
      action: PayloadAction<Partial<Medication> | undefined>
    ) => {
      mergeMedicationDrawerMedication(state, action.payload);
    },
    changeMedicationDrawerMedication: (
      state: Draft<AppContextState>,
      action: PayloadAction<Partial<Medication> | undefined>
    ) => {
      mergeMedicationDrawerMedication(state, action.payload);
      state.medicationDrawer.dryRunCompleted = false;
      state.medicationDrawer.automationOverridden = false;
    },
    overrideMedicationDrawerInterruption: (
      state: Draft<AppContextState>,
      action: PayloadAction<Partial<Medication> | undefined>
    ) => {
      mergeMedicationDrawerMedication(state, action.payload);
      state.medicationDrawer.applyAutomation = false;
      state.medicationDrawer.automationOverridden = true;
    },
    updateMedicationDrawerApplyAutomation: (
      state: Draft<AppContextState>,
      action: PayloadAction<boolean>
    ) => {
      state.medicationDrawer.applyAutomation = action.payload;
    },
    setMedicationDrawerDryRunStatus: (
      state: Draft<AppContextState>,
      action: PayloadAction<MedicationDrawerState['dryRunStatus']>
    ) => {
      state.medicationDrawer.dryRunStatus = action.payload;
    },
    setMedicationDrawerDryRunCompleted: (
      state: Draft<AppContextState>,
      action: PayloadAction<boolean>
    ) => {
      state.medicationDrawer.dryRunCompleted = action.payload;
    },
    updateMedicationDrawerDosageMatrix: (
      state: Draft<AppContextState>,
      action: PayloadAction<Partial<DosageMatrixValue>>
    ) => {
      if (!state.medicationDrawer.dosageMatrix) {
        state.medicationDrawer.dosageMatrix = defaultMatrixValue;
      }

      Object.entries(action.payload).forEach(([k, val]) => {
        const croppedVal =
          typeof val === 'string' ? val.slice(0, INPUT_MAX_LEN) : val;
        state.medicationDrawer.dosageMatrix = {
          ...state.medicationDrawer.dosageMatrix!,
          [k as TimeOfDay]: croppedVal
        };
      });

      // state.medicationDrawer.medicationDirty = isMedicationDirty(
      //   state.medicationDrawer
      // );
    },
    openScoresDrawer: (
      state: Draft<AppContextState>,
      action: PayloadAction<Omit<MedicalScoresDrawerState, 'open'>>
    ) => {
      setAllDrawersClosed(state);
      state.medicalScoresDrawer = {
        ...state.medicalScoresDrawer,
        ...action.payload,
        scoreName: action.payload.scoreName,
        open: true
      };
    },
    closeScoresDrawer: (state: Draft<AppContextState>) => {
      state.medicalScoresDrawer = {
        ...state.medicalScoresDrawer,
        open: false,
        patient: undefined
      };
    },
    setScoresDrawerType: (
      state: Draft<AppContextState>,
      action: PayloadAction<MedicalScoreName>
    ) => {
      state.medicalScoresDrawer.scoreName = action.payload;
    },
    setScoresPreferShowDraft: (
      state: Draft<AppContextState>,
      action: PayloadAction<boolean>
    ) => {
      state.medicalScoresDrawer.preferShowDraft = action.payload;
    },
    openScSuggestionDrawer: (
      state: Draft<AppContextState>,
      action: PayloadAction<Omit<ScSuggestionDrawerState, 'open'>>
    ) => {
      setAllDrawersClosed(state);
      state.scSuggestionDrawer = {
        ...action.payload,
        open: true
      };
    },
    closeScSuggestionDrawer: (state: Draft<AppContextState>) => {
      state.scSuggestionDrawer.open = false;
    },
    patchScSuggestionPayload: (
      state: Draft<AppContextState>,
      action: PayloadAction<Record<string, string | number | string[]>>
    ) => {
      const isEmptyOrNullOrUndefined = (v: (typeof action.payload)[string]) =>
        Array.isArray(v) ? !v?.length : isNil(v);
      state.scSuggestionDrawer.payload = omitBy(isEmptyOrNullOrUndefined, {
        ...state.scSuggestionDrawer.payload,
        ...action.payload
      });
    },
    setScSuggestionNotes: (
      state: Draft<AppContextState>,
      action: PayloadAction<string>
    ) => {
      if (action.payload) {
        state.scSuggestionDrawer.notes = action.payload;
      } else {
        delete state.scSuggestionDrawer.notes;
      }
    },
    clearScSuggestionDrawer: (state: Draft<AppContextState>) => {
      state.scSuggestionDrawer.payload = undefined;
      state.scSuggestionDrawer.notes = undefined;
    },
    openProtocolDrawer: (
      state: Draft<AppContextState>,
      action: PayloadAction<Omit<ProtocolDrawerState, 'open'>>
    ) => {
      setAllDrawersClosed(state);
      const { headerChipProps, ...restPayload } = action.payload;
      state.protocolDrawer = { ...restPayload, open: true };
      state.protocolDrawer.headerChipProps = headerChipProps as any;
    },
    closeProtocolDrawer: (state: Draft<AppContextState>) => {
      state.protocolDrawer = {
        open: false,
        headerChipProps: undefined,
        data: undefined,
        type: undefined
      };
    },
    patchProtocolDrawerData: (
      state: Draft<AppContextState>,
      action: PayloadAction<Record<string, any>>
    ) => {
      if (state.protocolDrawer.data) {
        state.protocolDrawer.data = {
          ...state.protocolDrawer.data,
          ...action.payload
        };
      }
    },
    openTemplateDrawer: (
      state: Draft<AppContextState>,
      action: PayloadAction<Omit<TemplateDrawerState, 'open'>>
    ) => {
      setAllDrawersClosed(state);
      state.templateDrawer = { ...action.payload, open: true };
    },
    closeTemplateDrawer: (state: Draft<AppContextState>) => {
      state.templateDrawer.open = false;
    },
    setTemplateDrawerType: (
      state: Draft<AppContextState>,
      action: PayloadAction<MedicalTemplateType>
    ) => {
      state.templateDrawer.templateType = action.payload;
    },
    openNewPatientDrawer: (state: Draft<AppContextState>) => {
      setAllDrawersClosed(state);
      state.newPatientDrawer.open = true;
    },
    closeNewPatientDrawer: (state: Draft<AppContextState>) => {
      state.newPatientDrawer.open = false;
    },
    setNewPatientInsertionType: (
      state: Draft<AppContextState>,
      action: PayloadAction<InsertionType>
    ) => {
      state.newPatientDrawer.insertionType = action.payload ?? 'patient';
    },
    patchNewPatientFormValues: (
      state: Draft<AppContextState>,
      action: PayloadAction<Record<string, any>>
    ) => {
      Object.assign(state.newPatientDrawer.formValues, action.payload);
    },
    clearNewPatientFormValues: (state: Draft<AppContextState>) => {
      state.newPatientDrawer.formValues = {};
    },
    patchTriageContext: (
      state: Draft<AppContextState>,
      action: PayloadAction<Partial<TriageContext['context']>>
    ) => {
      state.triage.context = {
        ...state.triage.context,
        ...action.payload,
        preopView: state.triage.context.preopView
      };
    },
    updatePagination: (
      state: Draft<AppContextState>,
      action: PayloadAction<TriagePaginationPayload>
    ) => {
      const { group, status, ...pagination } = action.payload;
      const statusKey = getPaginationKey(group, status, state);
      const currentPagination: any = (state.triage.pagination[group] as any)?.[
        statusKey
      ];

      state.triage.pagination[group] = {
        ...state.triage.pagination[group],
        [statusKey]: {
          ...currentPagination,
          ...pagination,
          page: pagination.page ?? currentPagination?.page ?? 0
        }
      };
    },
    updateLastSpecificDatePagination: (
      state: Draft<AppContextState>,
      action: PayloadAction<Partial<TablePagination>>
    ) => {
      state.triage.lastSpecificDatePagination = {
        ...state.triage.lastSpecificDatePagination,
        ...action.payload
      };
    },
    resetPagination: (
      state: Draft<AppContextState>,
      action: PayloadAction<{ group: TriageGroup }>
    ) => {
      const { group } = action.payload;
      state.triage.pagination[group] = undefined;
    },
    updateSorting: (
      state: Draft<AppContextState>,
      action: PayloadAction<TriageSortingPayload>
    ) => {
      const { group, status, ...sorting } = action.payload;
      const statusKey = getPaginationKey(group, status, state);
      const currentSorting: any = (state.triage.pagination[group] as any)?.[
        statusKey
      ];
      state.triage.pagination[group] = {
        ...state.triage.pagination[group],
        [statusKey]: {
          ...currentSorting,
          ...sorting
        }
      };
    },
    updateProtocolSearchKey: (
      state: Draft<AppContextState>,
      action: PayloadAction<string>
    ) => {
      const shouldResetPagination = !looseStringEquals(
        state.triage.protocolSearch,
        action.payload
      );
      state.triage.protocolSearch = action.payload;
      if (shouldResetPagination) {
        Object.values(state.triage.pagination)
          .map((group) => Object.values(group))
          .flat()
          .forEach((p) => (p.page = 0));
        if (state.triage.lastSpecificDatePagination) {
          state.triage.lastSpecificDatePagination.page = 0;
        }
      }
    },
    patchTriageEditable: (
      state: Draft<AppContextState>,
      action: PayloadAction<Partial<TriageContext['editable']>>
    ) => {
      state.triage.editable = {
        ...state.triage.editable,
        ...action.payload
      };
    },
    setAllTriageEditable: (
      state: Draft<AppContextState>,
      action: PayloadAction<boolean>
    ) => {
      const currentState = state.triage.editable;
      state.triage.editable = Object.keys(currentState).reduce(
        (acc, key) => ({ ...acc, [key]: action.payload }),
        currentState
      );
    },

    setCurrentHospital: (
      state: Draft<AppContextState>,
      action: PayloadAction<ID | undefined>
    ) => {
      state.currentHospital = action.payload;
    },

    setInitialMessageContent: (
      state: Draft<AppContextState>,
      action: PayloadAction<string | undefined>
    ) => {
      state.communication.initialMessage = action.payload;
    },

    patchUserScratchConsultationEligibility: (
      state,
      action: PayloadAction<User | null>
    ) => {
      updateUserScratchConsultationEligibility(state, action?.payload);
    },

    openPatientsAppDetailsDrawer: (
      state: Draft<AppContextState>,
      action: PayloadAction<{ patientId: ID }>
    ) => {
      setAllDrawersClosed(state);
      state.patientsAppDetailsDrawer = {
        open: true,
        patientId: action.payload.patientId
      };
    },
    closePatientsAppDetailsDrawer: (state: Draft<AppContextState>) => {
      state.patientsAppDetailsDrawer.open = false;
      state.patientsAppDetailsDrawer.patientId = undefined;
    },
    openCustomDocumentDrawer: (
      state: Draft<AppContextState>,
      action: PayloadAction<Omit<CustomDocumentDrawerState, 'open'>>
    ) => {
      setAllDrawersClosed(state);
      state.customDocumentDrawer = { ...action.payload, open: true };
    },
    closeCustomDocumentDrawer: (state: Draft<AppContextState>) => {
      state.customDocumentDrawer.open = false;
      state.customDocumentDrawer.patientId = undefined;
    },
    openBillingPatientDrawer: (
      state: Draft<AppContextState>,
      action: PayloadAction<{ patientId: ID; patientName?: string }>
    ) => {
      setAllDrawersClosed(state);
      state.billingPatientDrawer = { ...action.payload, open: true };
    },
    closeBillingPatientDrawer: (state: Draft<AppContextState>) => {
      // Payload kept on purpose (same reason as scSuggestionDrawer): the content stays
      // mounted through the ~250ms slide-out, so clearing it here would flash an empty
      // "unknown patient / 0,00 €" panel on the way out. The next open replaces it.
      state.billingPatientDrawer.open = false;
    }
  },
  extraReducers: (builder) => {
    builder.addCase(pingCurrentUser.pending, (state) => {
      state.triage.scratchConsultationEligibility.isUserNotRestricted = false;
    });

    builder.addCase(pingCurrentUser.fulfilled, (state, action) => {
      updateUserScratchConsultationEligibility(state, action.payload);
    });

    builder.addMatcher(
      doctorBffApi.endpoints?.userConfig.matchFulfilled,
      (state, action) => {
        state.triage.context.preopView = action.payload?.settings?.preopView;
      }
    );

    builder.addMatcher(
      doctorBffApi.endpoints?.updateUserAppSettings.matchPending,
      (state, action) => {
        state.triage.context.preopView =
          action.meta.arg.originalArgs?.preopView;
      }
    );
    builder.addMatcher(
      isAnyOf(
        surgicalEventApi.endpoints.activeSurgicalEvent.matchFulfilled,
        epicSlice.endpoints.createEpicClinicalNote.matchFulfilled
      ),
      (state) => {
        state.epicDocumentsDrawer.tmpEncounterId = undefined;
        state.epicDocumentsDrawer.tmpClinicalNoteTitle = undefined;
        state.epicDocumentsDrawer.tmpClinicalNote = undefined;
      }
    );

    builder.addMatcher(
      protocolApi.endpoints.protocol.matchFulfilled,
      (state, action) => {
        if (
          action.meta.arg.originalArgs.protocolName ===
          ProtocolName.SURGERY_PLANNING
        ) {
          const protocol = action.payload as SurgeryPlanningProtocol;
          state.triage.scratchConsultationEligibility.isNotSeen =
            !protocol.seenAt;
        }
      }
    );

    builder.addMatcher(
      protocolApi.endpoints.updateProtocol.matchFulfilled,
      (state, action) => {
        // An empty payload (no id) means the user cancelled the transition
        if (
          action.meta.arg.originalArgs.protocol ===
            ProtocolName.SURGERY_PLANNING &&
          action.payload.id
        ) {
          const protocol = action.payload as SurgeryPlanningProtocol;
          state.triage.scratchConsultationEligibility.isNotSeen =
            !protocol.seenAt;
        }
      }
    );

    builder.addMatcher(
      medsApi.endpoints.upsertMed.matchFulfilled,
      (state, action) => {
        const responseMeds = action.payload;
        let newMed: Medication | undefined;
        if (responseMeds.length <= 1) {
          newMed = responseMeds[0];
        } else {
          const existingId = action.meta.arg.originalArgs.medication.id;
          if (existingId) {
            newMed = responseMeds.find((m) => m.id === existingId);
          } else {
            newMed = responseMeds.sort(
              (a, b) => -a.lastModifiedDate.localeCompare(b.lastModifiedDate)
            )[0];
          }
        }

        if (newMed) {
          state.medicationDrawer.applyAutomation = !isEmpty(
            newMed.missingAutomationProperties
          );
          const { dosage, matrixValue } =
            deserializeDosageAndMatrixValues(newMed);
          state.medicationDrawer.medication = {
            ...newMed,
            dosage
          };
          state.medicationDrawer.persistedMedication = newMed;
          state.medicationDrawer.dosageMatrix =
            matrixValue ?? defaultMatrixValue;
        }

        // Reset medication instructions drawer when medications change
        state.medicationInstructionsDrawer.initialText = undefined;
        state.medicationInstructionsDrawer.editedText = undefined;
      }
    );

    builder.addMatcher(medsApi.endpoints.deleteMed.matchFulfilled, (state) => {
      // Reset medication instructions drawer when medications are deleted
      state.medicationInstructionsDrawer.initialText = undefined;
      state.medicationInstructionsDrawer.editedText = undefined;
    });
  }
});

export const {
  closeAllDrawers,
  patchTriageContext,
  patchTriageEditable,
  setAllTriageEditable,
  openEpicDocumentsDrawer,
  setEpicDocumentDrawerDocument,
  setEpicDocumentDrawerNlpView,
  updateEpicClinicalNote,
  updateEpicClinicalNoteTitle,
  updateEpicClinicalNoteEncounterId,
  closeEpicDocumentsDrawer,
  openMedicationInstructionsDrawer,
  closeMedicationInstructionsDrawer,
  updateMedicationInstructionsText,
  openMedicationDrawer,
  updateMedicationDrawerMedication,
  changeMedicationDrawerMedication,
  overrideMedicationDrawerInterruption,
  updateMedicationDrawerApplyAutomation,
  updateMedicationDrawerDosageMatrix,
  setMedicationDrawerDryRunStatus,
  setMedicationDrawerDryRunCompleted,
  closeMedicationDrawer,
  clearMedicationDrawer,
  openPremedicationDrawer,
  closePremedicationDrawer,
  openScoresDrawer,
  closeScoresDrawer,
  setScoresDrawerType,
  setScoresPreferShowDraft,
  openScSuggestionDrawer,
  patchScSuggestionPayload,
  setScSuggestionNotes,
  closeScSuggestionDrawer,
  openProtocolDrawer,
  closeProtocolDrawer,
  patchProtocolDrawerData,
  openTemplateDrawer,
  closeTemplateDrawer,
  setTemplateDrawerType,
  openNewPatientDrawer,
  closeNewPatientDrawer,
  setNewPatientInsertionType,
  patchNewPatientFormValues,
  clearNewPatientFormValues,
  updatePagination,
  updateLastSpecificDatePagination,
  resetPagination,
  updateSorting,
  updateProtocolSearchKey,
  setCurrentHospital,
  setInitialMessageContent,
  patchUserScratchConsultationEligibility,
  _setTempListenForSurgeryPlanningProtocolStatusUpdates,
  openPatientsAppDetailsDrawer,
  closePatientsAppDetailsDrawer,
  openCustomDocumentDrawer,
  closeCustomDocumentDrawer,
  openBillingPatientDrawer,
  closeBillingPatientDrawer
} = appContextSlice.actions;

export const clearInitialMessageContent = () =>
  setInitialMessageContent(undefined);

/**
 * This method returns the key to use to update/read pagination state.
 * This is required in order to discriminate various specific dates in PREOP_ALL group.
 * For every other group, the given status will be returned
 */
export const getPaginationKey = (
  group: TriageGroup,
  status: DashboardGroupingStatus,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  state: Draft<AppContextState> | AppContextState
) =>
  // kept in case we need a more specific key again
  // if (group === TriageGroup.PREOP_ALL) {
  //   const view = state.triage.context.preopView;
  //   if (view === PreopView.DAY) {
  //     const date = state.triage.context.specificDate;
  //     return `${status}_${date}`;
  //   }
  // }

  status;

function looseStringEquals(s1?: string, s2?: string) {
  if (!s1 && !s2) {
    return true;
  }
  return s1 === s2;
}

function updateUserScratchConsultationEligibility(
  state: Draft<AppContextState>,
  user: User | null
) {
  state.triage.scratchConsultationEligibility.isUserNotRestricted =
    !!user && !isUserRestricted(user);
}

export const appContextReducer = appContextSlice.reducer;
